import type { EnemyRoleDef } from "../content/schemas";
import type { EnemyState, ShipState } from "./snapshot";
import { clamp, len2, normalize2, vec2 } from "./math";

const TAU = Math.PI * 2;

export function defaultAiState(roleId: EnemyRoleDef["id"]): EnemyState["aiState"] {
  switch (roleId) {
    case "scout":
      return "orbit";
    case "skirmisher":
      return "flank";
    case "turret":
      return "suppress";
    case "bomber":
      return "approach";
    case "breacher":
    case "marauder":
      return "rush";
    case "jammer":
    case "tender":
    case "sapper":
    case "nullifier":
    case "leech":
      return "orbit";
    case "carrier":
    case "scavenger":
      return "approach";
    case "sniper":
    case "warden":
    case "harvester":
    case "sovereign":
    case "leviathan":
    case "null_crown":
    case "centurion":
      return "suppress";
    default:
      return "approach";
  }
}

function applyVelocity(
  enemy: EnemyState,
  vx: number,
  vy: number,
  dt: number,
  faceDir?: { x: number; y: number },
): void {
  enemy.vx = vx;
  enemy.vy = vy;
  enemy.x += enemy.vx * dt;
  enemy.y += enemy.vy * dt;
  if (faceDir && len2(faceDir) > 0.01) {
    enemy.facing = Math.atan2(faceDir.y, faceDir.x);
  }
}

/** Periodic afterburn windows for aggressive roles — returns speed mult + rising-edge pulse. */
export function afterburnWindow(
  aiTimer: number,
  period: number,
  window: number,
  dt: number,
): { mult: number; pulse: boolean } {
  const phase = ((aiTimer % period) + period) % period;
  const boosting = phase < window;
  const prevPhase = (((aiTimer - dt) % period) + period) % period;
  const pulse = boosting && prevPhase >= window;
  // Keep surge readable — 1.55× on already-fast roles felt like teleporting dots.
  return { mult: boosting ? 1.32 : 1, pulse };
}

export function updateEnemyMotion(
  enemy: EnemyState,
  role: EnemyRoleDef,
  player: ShipState,
  dt: number,
): { fireReady: boolean; meleeDamage: number; boostPulse: boolean } {
  const toPlayer = vec2(player.x - enemy.x, player.y - enemy.y);
  const distance = len2(toPlayer);
  const dir = distance > 0.01 ? normalize2(toPlayer) : vec2(1, 0);
  let fireReady = false;
  let meleeDamage = 0;

  if (role.id === "turret") {
    enemy.vx = 0;
    enemy.vy = 0;
    enemy.aiState = "suppress";
    if (distance > 0.1) enemy.facing = Math.atan2(dir.y, dir.x);
    // Hold fire until player is in a sensible engagement cone/range.
    fireReady = distance > 18 && distance < 95;
    return { fireReady: fireReady && enemy.telegraphTimer <= 0, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "breacher" || role.id === "marauder") {
    const boarding = role.id === "marauder";
    const recovering = enemy.fireCooldown > role.fireCooldown * (boarding ? 0.5 : 0.4);
    if (recovering) {
      enemy.aiState = "retreat";
      applyVelocity(
        enemy,
        -dir.x * role.speed * (boarding ? 0.82 : 1.08),
        -dir.y * role.speed * (boarding ? 0.82 : 1.08),
        dt,
        vec2(-dir.x, -dir.y),
      );
      return { fireReady: false, meleeDamage: 0, boostPulse: false };
    }
    enemy.aiState = "rush";
    const burn = afterburnWindow(enemy.aiTimer, boarding ? 3.8 : 3.1, boarding ? 0.44 : 0.32, dt);
    const weave =
      Math.sin(enemy.aiTimer * (boarding ? 4.2 : 7) + enemy.id) * (boarding ? 0.18 : 0.28);
    const side = normalize2(vec2(-dir.y, dir.x));
    const speed = role.speed * Math.min(burn.mult, boarding ? 1.24 : 1.18);
    applyVelocity(
      enemy,
      (dir.x + side.x * weave) * speed,
      (dir.y + side.y * weave) * speed,
      dt,
      dir,
    );
    if (distance < (boarding ? 3.8 : 3.4) && enemy.fireCooldown <= 0) {
      meleeDamage = role.damage * (enemy.elite ? 1.25 : 1);
      enemy.fireCooldown = role.fireCooldown;
    }
    return { fireReady: false, meleeDamage, boostPulse: burn.pulse };
  }

  if (role.id === "bomber") {
    const lowHull = enemy.hull < enemy.maxHull * 0.45;
    if (lowHull || distance < 32) {
      enemy.aiState = "retreat";
      const bank = Math.sin(enemy.aiTimer * 2.2) * 0.4;
      const side = normalize2(vec2(-dir.y, dir.x));
      applyVelocity(
        enemy,
        (-dir.x + side.x * bank) * role.speed * 1.15,
        (-dir.y + side.y * bank) * role.speed * 1.15,
        dt,
        vec2(-dir.x, -dir.y),
      );
    } else {
      enemy.aiState = "approach";
      // Wide S-curve approach to preferred stand-off.
      const desired = 48;
      const radial = distance > desired ? 1 : distance < desired - 10 ? -1 : 0;
      const bank = Math.sin(enemy.aiTimer * 1.4 + enemy.id * 0.7) * 0.55;
      const side = normalize2(vec2(-dir.y, dir.x));
      applyVelocity(
        enemy,
        (dir.x * radial + side.x * bank) * role.speed,
        (dir.y * radial + side.y * bank) * role.speed,
        dt,
        dir,
      );
    }
    fireReady = distance < 85 && distance > 28;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "scout") {
    // Kite: keep distance, orbit, fire while peeling away; peel-boost when too close.
    const desired = 30;
    const tooClose = distance < desired - 8;
    const tooFar = distance > desired + 10;
    enemy.aiState = tooClose ? "retreat" : "orbit";
    const burn = afterburnWindow(enemy.aiTimer + enemy.id * 0.37, 3.4, 0.38, dt);
    const tangent = normalize2(vec2(-dir.y, dir.x));
    const radial = tooFar ? 0.7 : tooClose ? -1.1 : 0.15;
    const orbit = tooClose ? 0.35 : 1;
    const speed = role.speed * (tooClose ? Math.max(burn.mult, 1.18) : burn.mult);
    applyVelocity(
      enemy,
      (dir.x * radial + tangent.x * orbit) * speed,
      (dir.y * radial + tangent.y * orbit) * speed,
      dt,
      tooClose ? vec2(-dir.x, -dir.y) : dir,
    );
    fireReady = distance < 70 && distance > 16 && enemy.burstCd <= 0;
    return { fireReady, meleeDamage: 0, boostPulse: burn.pulse };
  }

  if (role.id === "skirmisher") {
    // Flank: hold a side pocket, swap shoulders periodically; strafing afterburn.
    enemy.aiState = "flank";
    const burn = afterburnWindow(enemy.aiTimer + enemy.id * 0.21, 3.1, 0.36, dt);
    const sideSign = Math.sin(enemy.aiTimer * 0.35 + enemy.id) >= 0 ? 1 : -1;
    const flankAngle = player.facing + sideSign * 1.85;
    const pocket = 28 + (enemy.id % 3) * 4;
    const target = vec2(
      player.x + Math.cos(flankAngle) * pocket,
      player.y + Math.sin(flankAngle) * pocket,
    );
    const toPocket = vec2(target.x - enemy.x, target.y - enemy.y);
    const pocketDist = len2(toPocket);
    const flankDir = pocketDist > 0.01 ? normalize2(toPocket) : dir;
    // Drift toward pocket; when close, strafe for a shooting pass.
    const strafe = pocketDist < 10 ? normalize2(vec2(-dir.y, dir.x)) : vec2(0, 0);
    const speed = role.speed * burn.mult;
    applyVelocity(
      enemy,
      (flankDir.x * 0.85 + strafe.x * 0.55) * speed,
      (flankDir.y * 0.85 + strafe.y * 0.55) * speed,
      dt,
      dir,
    );
    fireReady = distance < 78 && distance > 14;
    return { fireReady, meleeDamage: 0, boostPulse: burn.pulse };
  }

  if (role.id === "jammer") {
    // Wide orbit to keep aura up without closing for a brawl.
    enemy.aiState = "orbit";
    const desired = 36;
    const tangent = normalize2(vec2(-dir.y, dir.x));
    const radial = distance > desired ? 0.55 : distance < desired - 12 ? -0.7 : 0;
    applyVelocity(
      enemy,
      (dir.x * radial + tangent.x * 0.9) * role.speed,
      (dir.y * radial + tangent.y * 0.9) * role.speed,
      dt,
      dir,
    );
    fireReady = distance < 70;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "tender") {
    // Orbit the formation at support range; the simulation restores nearby
    // allied shields while the tender remains alive.
    enemy.aiState = "orbit";
    const desired = 44;
    const tangent = normalize2(vec2(-dir.y, dir.x));
    const radial = distance > desired + 8 ? 0.5 : distance < desired - 10 ? -0.65 : 0;
    const sideSign = enemy.id % 2 === 0 ? 1 : -1;
    applyVelocity(
      enemy,
      (dir.x * radial + tangent.x * 0.72 * sideSign) * role.speed,
      (dir.y * radial + tangent.y * 0.72 * sideSign) * role.speed,
      dt,
      dir,
    );
    fireReady = distance > 22 && distance < 82;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "sapper") {
    // Keeps a deliberate mid-range drain lane so its fuel-venting projectiles
    // can be read and dodged instead of blending into close-range pressure.
    enemy.aiState = "orbit";
    const desired = 58;
    const tangent = normalize2(vec2(-dir.y, dir.x));
    const radial = distance > desired + 10 ? 0.62 : distance < desired - 12 ? -0.78 : 0;
    const sideSign = enemy.id % 2 === 0 ? 1 : -1;
    applyVelocity(
      enemy,
      (dir.x * radial + tangent.x * 0.62 * sideSign) * role.speed,
      (dir.y * radial + tangent.y * 0.62 * sideSign) * role.speed,
      dt,
      dir,
    );
    fireReady = distance > 30 && distance < 105;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "nullifier") {
    // Holds the edge of its anti-guidance field so the player can either close
    // and remove it or fight without homing assistance.
    enemy.aiState = "orbit";
    const desired = 46;
    const tangent = normalize2(vec2(-dir.y, dir.x));
    const radial = distance > desired + 8 ? 0.58 : distance < desired - 10 ? -0.7 : 0;
    const sideSign = enemy.id % 2 === 0 ? 1 : -1;
    applyVelocity(
      enemy,
      (dir.x * radial + tangent.x * 0.68 * sideSign) * role.speed,
      (dir.y * radial + tangent.y * 0.68 * sideSign) * role.speed,
      dt,
      dir,
    );
    fireReady = distance > 24 && distance < 92;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "leech") {
    // Maintains a readable drain lane: close enough to threaten shields, far
    // enough that its return fire can be dodged before it self-repairs.
    enemy.aiState = "orbit";
    const desired = 52;
    const tangent = normalize2(vec2(-dir.y, dir.x));
    const radial = distance > desired + 9 ? 0.6 : distance < desired - 11 ? -0.74 : 0;
    const sideSign = enemy.id % 2 === 0 ? 1 : -1;
    applyVelocity(
      enemy,
      (dir.x * radial + tangent.x * 0.66 * sideSign) * role.speed,
      (dir.y * radial + tangent.y * 0.66 * sideSign) * role.speed,
      dt,
      dir,
    );
    fireReady = distance > 26 && distance < 98;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "carrier") {
    // Slow lumbering push — stay high, don't chase hard.
    enemy.aiState = "approach";
    const desired = 55;
    const radial = distance > desired ? 0.75 : distance < desired - 14 ? -0.4 : 0;
    const drift = Math.sin(enemy.aiTimer * 0.8) * 0.25;
    const side = normalize2(vec2(-dir.y, dir.x));
    applyVelocity(
      enemy,
      (dir.x * radial + side.x * drift) * role.speed,
      (dir.y * radial + side.y * drift) * role.speed,
      dt,
      dir,
    );
    fireReady = distance < 75;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "sniper") {
    // Long-range rail platform: backs out of brawls, then strafes between deliberate shots.
    enemy.aiState = "suppress";
    const desired = 72;
    const radial = distance > desired + 12 ? 0.75 : distance < desired - 14 ? -1 : 0;
    const side = normalize2(vec2(-dir.y, dir.x));
    const strafe = Math.sin(enemy.aiTimer * 0.9 + enemy.id) * 0.42;
    applyVelocity(
      enemy,
      (dir.x * radial + side.x * strafe) * role.speed,
      (dir.y * radial + side.y * strafe) * role.speed,
      dt,
      dir,
    );
    fireReady = distance > 42 && distance < 118;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "scavenger") {
    // The simulation supplies the nearest loose-salvage position as the target.
    // With no loose scrap it shadows the player, ready to contest the next drop.
    enemy.aiState = "approach";
    const slowForPickup = distance < 4 ? 0.3 : 1;
    applyVelocity(
      enemy,
      dir.x * role.speed * slowForPickup,
      dir.y * role.speed * slowForPickup,
      dt,
      dir,
    );
    return { fireReady: false, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "warden") {
    // Armored arena platform: holds a mid-range broadside orbit rather than
    // chasing, leaving room to read its fan volleys and reinforcement phases.
    enemy.aiState = "suppress";
    const desired = 48;
    const radial = distance > desired + 10 ? 0.65 : distance < desired - 12 ? -0.7 : 0;
    const side = normalize2(vec2(-dir.y, dir.x));
    const orbit = Math.sin(enemy.aiTimer * 0.55 + enemy.id) * 0.55;
    applyVelocity(
      enemy,
      (dir.x * radial + side.x * orbit) * role.speed,
      (dir.y * radial + side.y * orbit) * role.speed,
      dt,
      dir,
    );
    fireReady = distance > 24 && distance < 96;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "harvester") {
    // Heavy salvage processor: maintains an oppressive mid-range lane while
    // the simulation pulls loose cargo into its visible intake.
    enemy.aiState = "suppress";
    const desired = 43;
    const radial = distance > desired + 9 ? 0.6 : distance < desired - 11 ? -0.65 : 0;
    const side = normalize2(vec2(-dir.y, dir.x));
    const orbit = Math.sin(enemy.aiTimer * 0.42 + enemy.id) * 0.4;
    applyVelocity(
      enemy,
      (dir.x * radial + side.x * orbit) * role.speed,
      (dir.y * radial + side.y * orbit) * role.speed,
      dt,
      dir,
    );
    fireReady = distance > 20 && distance < 92;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "sovereign") {
    enemy.aiState = "suppress";
    const desired = 50;
    const radial = distance > desired + 8 ? 0.65 : distance < desired - 10 ? -0.72 : 0;
    const side = normalize2(vec2(-dir.y, dir.x));
    const orbit = Math.sin(enemy.aiTimer * 0.5 + enemy.id) * 0.62;
    applyVelocity(
      enemy,
      (dir.x * radial + side.x * orbit) * role.speed,
      (dir.y * radial + side.y * orbit) * role.speed,
      dt,
      dir,
    );
    fireReady = distance > 22 && distance < 105;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "leviathan") {
    // Massive mobile foundry: slowly rotates around a long firing lane while
    // its boarding craft create the close-range pressure.
    enemy.aiState = "suppress";
    const desired = 55;
    const radial = distance > desired + 10 ? 0.55 : distance < desired - 12 ? -0.62 : 0;
    const side = normalize2(vec2(-dir.y, dir.x));
    const orbit = Math.sin(enemy.aiTimer * 0.36 + enemy.id) * 0.46;
    applyVelocity(
      enemy,
      (dir.x * radial + side.x * orbit) * role.speed,
      (dir.y * radial + side.y * orbit) * role.speed,
      dt,
      dir,
    );
    fireReady = distance > 24 && distance < 110;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "null_crown") {
    // A deliberate long-range orbit lets its linked suppression lattice occupy
    // the space between the player and the boss.
    enemy.aiState = "suppress";
    const desired = 58;
    const radial = distance > desired + 9 ? 0.58 : distance < desired - 11 ? -0.66 : 0;
    const side = normalize2(vec2(-dir.y, dir.x));
    const orbit = Math.sin(enemy.aiTimer * 0.44 + enemy.id) * 0.54;
    applyVelocity(
      enemy,
      (dir.x * radial + side.x * orbit) * role.speed,
      (dir.y * radial + side.y * orbit) * role.speed,
      dt,
      dir,
    );
    fireReady = distance > 25 && distance < 116;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "centurion") {
    // Keeps a wide arena lane so its counterpulse boundary and retaliation
    // wave are readable before the player commits another projectile burst.
    enemy.aiState = "suppress";
    const desired = 62;
    const radial = distance > desired + 10 ? 0.55 : distance < desired - 12 ? -0.64 : 0;
    const side = normalize2(vec2(-dir.y, dir.x));
    const orbit = Math.sin(enemy.aiTimer * 0.38 + enemy.id) * 0.48;
    applyVelocity(
      enemy,
      (dir.x * radial + side.x * orbit) * role.speed,
      (dir.y * radial + side.y * orbit) * role.speed,
      dt,
      dir,
    );
    fireReady = distance > 26 && distance < 122;
    return { fireReady, meleeDamage: 0, boostPulse: false };
  }

  if (role.id === "ace") {
    // Aggressive corkscrew intercept with frequent afterburn.
    enemy.aiState = "flank";
    const burn = afterburnWindow(enemy.aiTimer, 2.2, 0.4, dt);
    const desired = 26;
    const radial = distance > desired ? 1 : distance < desired - 6 ? -0.6 : 0.2;
    const spin = Math.sin(enemy.aiTimer * 3.2) * 0.75;
    const side = normalize2(vec2(-dir.y, dir.x));
    const speed = role.speed * burn.mult;
    applyVelocity(
      enemy,
      (dir.x * radial + side.x * spin) * speed,
      (dir.y * radial + side.y * spin) * speed,
      dt,
      dir,
    );
    fireReady = distance < 90;
    return { fireReady, meleeDamage: 0, boostPulse: burn.pulse };
  }

  // Fallback
  if (role.speed > 0) {
    const desired = 34;
    const sign = distance > desired ? 1 : distance < desired - 8 ? -1 : 0;
    applyVelocity(enemy, dir.x * role.speed * sign, dir.y * role.speed * sign, dt, dir);
  }
  if (distance > 0.1) enemy.facing = Math.atan2(dir.y, dir.x);
  fireReady = distance < 90;
  return { fireReady, meleeDamage: 0, boostPulse: false };
}

export function leadAimDirection(
  enemy: EnemyState,
  player: ShipState,
  projectileSpeed: number,
): { x: number; y: number } {
  const toPlayer = vec2(player.x - enemy.x, player.y - enemy.y);
  const dist = len2(toPlayer);
  if (dist < 0.01 || projectileSpeed <= 0) return normalize2(toPlayer);
  const tx = player.x + player.vx * (dist / projectileSpeed);
  const ty = player.y + player.vy * (dist / projectileSpeed);
  return normalize2(vec2(tx - enemy.x, ty - enemy.y));
}

export function wrapAngle(a: number): number {
  return ((a % TAU) + TAU) % TAU;
}

export function clamp01(v: number): number {
  return clamp(v, 0, 1);
}
